<template>
  <FrontendLayoutAccount>
    <template #title>Favorilerim</template>

    <!-- Loading State -->
    <template v-if="loading">
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <FrontendSkeletonProductCard v-for="index in 8" :key="index" />
      </div>
    </template>

    <!-- Empty State -->
    <div v-else-if="!favorites?.length" class="py-12">
      <div class="text-center">
        <UIcon name="i-heroicons-heart" class="mx-auto h-12 w-12 text-gray-400" />
        <h3 class="mt-2 text-sm font-semibold text-gray-900">
          Favori ürününüz bulunmuyor
        </h3>
        <p class="mt-1 text-sm text-gray-500">
          Beğendiğiniz ürünleri favorilerinize ekleyerek takip edebilirsiniz.
        </p>
        <div class="mt-6">
          <UButton to="/" color="orange" variant="solid">
            Alışverişe Başla
          </UButton>
        </div>
      </div>
    </div>

    <!-- Favorites List -->
    <div v-else>
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <FrontendProductCardSelector v-for="favorite in favorites" :key="favorite.id" :product="favorite.product"
          @toggle-favorite="handleToggleFavorite(favorite.product)" />
      </div>

      <!-- Sayfalama kontrolleri -->
      <div v-if="pagination.lastPage > 1" class="mt-8 py-4 border-t border-gray-100">
        <div class="flex justify-between items-center">
          <span class="text-sm text-gray-600">
            Toplam {{ pagination.total }} favori ürün
          </span>

          <!-- UPagination Komponenti -->
          <UPagination v-model="currentPage" :total="pagination.total" :page-count="pagination.perPage" :max="7"
            show-first show-last class="mt-0" :to="(page) => ({
              path: route.path,
              query: {
                ...route.query,
                page
              }
            })" />
        </div>
      </div>
    </div>
  </FrontendLayoutAccount>
</template>

<script setup>
const loading = ref(true)
const favorites = ref([])
const route = useRoute()
const { fetchFavorites, toggleFavorite } = useFavorites()

// Sayfalama bilgileri
const pagination = ref({
  currentPage: 1,
  perPage: 20,
  total: 0,
  lastPage: 1
})

// Mevcut sayfa numarası (UPagination v-model için)
const currentPage = computed({
  get: () => pagination.value.currentPage,
  set: (page) => {
    // URL'yi güncelle, watch ile API'ye istek yapılacak
    navigateTo({
      path: route.path,
      query: { ...route.query, page }
    })
  }
})

// Favori durumunu değiştir
const handleToggleFavorite = async (product) => {
  await toggleFavorite(product)
  // Favorileri yeniden yükle (gerekirse)
  await fetchFavoritesHandler(pagination.value.currentPage)
}

useHead({
  title: 'Favorilerim | Beyraha',
  meta: [
    {
      name: 'description',
      content:
        'Beğendiğiniz ve favorilerinize eklediğiniz ürünleri bu sayfada görüntüleyebilirsiniz.'
    }
  ]
})

// Favorileri getir
const fetchFavoritesHandler = async (page = 1) => {
  loading.value = true
  try {
    const response = await fetchFavorites(page, pagination.value.perPage)

    // response içinde doğrudan favorites dizisi ve sayfalama bilgileri var
    favorites.value = response?.favorites || []

    // Sayfalama bilgilerini güncelle (response'ın kendisinde)
    pagination.value = {
      currentPage: response?.currentPage,
      perPage: response?.perPage,
      total: response?.total,
      lastPage: response?.lastPage
    }
  } catch (error) {
    console.error('Favorileri getirme hatası:', error)
    toast.error('Favoriler alınırken bir hata oluştu')
  } finally {
    loading.value = false
  }
}

// Sayfa yüklendiğinde favorileri getir
onMounted(() => {
  const page = parseInt(route.query.page) || 1
  fetchFavoritesHandler(page)
})

// URL parametreleri değiştiğinde favorileri yeniden getir
watch(() => route.query.page, (newPage) => {
  const page = parseInt(newPage) || 1
  fetchFavoritesHandler(page)
  // Sayfa değiştiğinde en yukarı kaydır
  window.scrollTo({ top: 0, behavior: 'smooth' })
})
</script>
